import { toAddressPayload } from '@/utils/address';
import { resolveHomeLocation } from '@/utils/homeLocation';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  Key,
  Users,
  UserCheck,
  UserX,
  AlertTriangle,
  Mail,
  Phone,
  Download,
  Upload,
} from 'lucide-react';
import { useTranslation } from '../../i18n';

import { useEmployeeStore } from '../../store/useEmployeeStore';
import { useToastStore } from '../../store/useToastStore';
import { useTimeclockAdminStore } from '../../store/useTimeclockAdminStore';
import { usePlanningStore } from '../../store/usePlanningStore';
import { useAbsenceStore } from '../../store/useAbsenceStore';

import { Button } from '../../components/Shared/Button';
import { EmptyState } from '../../components/Shared/EmptyState';
import { HelpPopover } from '../../components/Shared/HelpPopover';
import { Pagination } from '../../components/Shared/Pagination';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog';
import { EmployeeAvatar } from '../../components/Shared/EmployeeAvatar';
import { EmployeeDialog, type EmployeeFormData } from './components/EmployeeDialog';
import { timeClockGroupForCreate, timeClockGroupForUpdate } from './components/employeeTimeClockGroup';
import { PasswordResetDialog } from './components/PasswordResetDialog';
import { ExcelImportModal } from '../../components/Shared/ExcelImportModal';
import { useExcelIo } from '../../hooks/useExcelIo';

import { ListPageShell } from '../../components/Lists/ListPageShell';
import { KpiStripList } from '../../components/Lists/KpiStripList';
import { TabStrip } from '../../components/Lists/TabStrip';
import { ListToolbar } from '../../components/Lists/ListToolbar';
import { ListGrid } from '../../components/Lists/ListGrid';

import { useStaffListOps } from '../../hooks/useStaffListOps';
import { usePagedList } from '../../hooks/usePagedList';
import { useUrlListState } from '../../hooks/useUrlListState';
import { SORT_IDS, TAB_IDS, type SortId, type TabId } from './logic/employeeList';
import {
  getMondayOfWeek,
  formatHoursDecimal,
  weekDayIsoList,
  nowMinutes as nowMinutesFn,
  getCurrentTask,
} from '../../utils/dashboardData';
import { aggregateEmployeeWeek, summarizeEmployeeWeek } from '../../utils/listData';
import { getObjectColors } from '../../utils/objectColors';
import { formatUtcToLocalTime, isoDate } from '../../utils/dateUtils';
import { getISOWeek } from 'date-fns';

import { FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyAttendanceStatus as AttendanceStatus } from '../../api/generated/models';
import type {
  FacilityFlowCoreModulesEmployeesDtosEmployeeListDto,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyEmployeeStatusDto,
} from '../../api/generated/models';
import type { KpiItem, SortOption, TabItem } from '../../types/lists';

type Employee = FacilityFlowCoreModulesEmployeesDtosEmployeeListDto;
type DailyStatus = FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyEmployeeStatusDto;
type LiveStatus = 'in' | 'break' | 'absent' | 'missing' | 'office' | 'out';

const COLUMNS = 'minmax(0,1.4fr) minmax(0,1fr) 140px minmax(0,1fr) minmax(0,1.2fr) 168px';

export function Employees() {
  const { t } = useTranslation(['employees', 'common']);
  const navigate = useNavigate();
  const { addToast } = useToastStore();

  const {
    employees,
    currentEmployee,
    loading,
    error,
    fetchAllEmployees,
    fetchEmployeeById,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    activateEmployee,
    deactivateEmployee,
    resetPassword,
    clearError,
  } = useEmployeeStore();

  const dailyStatus = useTimeclockAdminStore((s) => s.dailyStatus);
  const planningEntries = usePlanningStore((s) => s.entries);
  const absences = useAbsenceStore((s) => s.absences);

  // Live polling + initial loads
  useStaffListOps({ pollMs: 60_000 });

  const [listState, setListState] = useUrlListState(
    { q: '', tab: 'all' as TabId, sort: 'name' as SortId, page: 1 },
    { tab: TAB_IDS, sort: SORT_IDS },
  );
  const { q: search, tab, sort: sortBy, page: activePage } = listState;
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
  const [selectedEmployeeForReset, setSelectedEmployeeForReset] = useState<{
    id: string;
    name: string;
    email?: string;
    phone?: string;
  } | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const { exporting, exportExcel } = useExcelIo('employees');
  const [employeeToDelete, setEmployeeToDelete] = useState<{ id: string; name: string } | null>(
    null,
  );

  const [nowMin, setNowMin] = useState<number>(() => nowMinutesFn());
  useEffect(() => {
    const id = setInterval(() => setNowMin(nowMinutesFn()), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetchAllEmployees().then(() => setInitialLoadComplete(true));
  }, [fetchAllEmployees]);

  const today = useMemo(() => new Date(), []);
  const monday = useMemo(() => getMondayOfWeek(today), [today]);
  const weekDays = useMemo(() => weekDayIsoList(monday), [monday]);
  const todayIso = useMemo(() => isoDate(today), [today]);
  const weekNumber = useMemo(() => getISOWeek(today), [today]);

  const todayPlanByEmployee = useMemo(() => {
    const map = new Map<string, typeof planningEntries[number]>();
    for (const e of planningEntries) {
      if (e.date === todayIso && e.employeeId) {
        map.set(e.employeeId, e);
      }
    }
    return map;
  }, [planningEntries, todayIso]);

  const weekEntries = useMemo(
    () => planningEntries.filter((e) => e.date && weekDays.includes(e.date)),
    [planningEntries, weekDays],
  );

  const monthEntries = planningEntries; // already loaded by hook

  const dailyByEmployee = useMemo(() => {
    const map = new Map<string, DailyStatus>();
    for (const e of dailyStatus?.employees ?? []) {
      if (e.employeeId) map.set(e.employeeId, e);
    }
    return map;
  }, [dailyStatus]);

  const absenceTodayByEmployee = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of absences) {
      if (
        a.approvalStatus === 'Approved' &&
        a.startDate &&
        a.endDate &&
        a.startDate <= todayIso &&
        a.endDate >= todayIso &&
        a.employeeId
      ) {
        map.set(a.employeeId, a.absenceTypeName ?? '—');
      }
    }
    return map;
  }, [absences, todayIso]);

  function deriveLiveStatus(emp: Employee, daily: DailyStatus | undefined): LiveStatus {
    if (emp.id && absenceTodayByEmployee.has(emp.id)) return 'absent';
    switch (daily?.status) {
      case AttendanceStatus.NUMBER_1:
        return emp.role === 'Verwaltung' ? 'office' : 'in';
      case AttendanceStatus.NUMBER_4:
        return 'break';
      case AttendanceStatus.NUMBER_3:
        return 'missing';
      case AttendanceStatus.NUMBER_5:
        return 'absent';
      case AttendanceStatus.NUMBER_2:
        return 'out';
      case AttendanceStatus.NUMBER_6:
      default:
        return 'out';
    }
  }

  const kpis: KpiItem[] = useMemo(() => {
    const totalActive = employees.filter((e) => e.status === 'Active').length;
    const inCount = employees.filter(
      (emp) => emp.id && ['in', 'office'].includes(deriveLiveStatus(emp, dailyByEmployee.get(emp.id))),
    ).length;
    const { targetHours, avgUtilization } = summarizeEmployeeWeek(employees, weekEntries, monthEntries, absences);
    return [
      {
        label: t('employees:kpis.active'),
        value: totalActive,
        sub: t('employees:kpis.ofTotal', { count: employees.length }),
      },
      { label: t('employees:kpis.clockedIn'), value: inCount, sub: t('employees:kpis.now') },
      { label: t('employees:kpis.hPerWeekPlanned'), value: Math.round(targetHours), unit: 'h' },
      {
        label: t('employees:kpis.avgUtilization'),
        value: avgUtilization,
        unit: '%',
        help: { label: t('common:actions.showExplanation'), text: t('employees:kpis.avgUtilizationHelp') },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, dailyByEmployee, weekEntries, monthEntries, absences, absenceTodayByEmployee, t]);

  const liveCounts = useMemo(() => {
    let inCount = 0;
    let missingCount = 0;
    for (const emp of employees) {
      if (!emp.id) continue;
      const live = deriveLiveStatus(emp, dailyByEmployee.get(emp.id));
      if (live === 'in' || live === 'office') inCount++;
      if (live === 'missing') missingCount++;
    }
    return { inCount, missingCount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, dailyByEmployee, absenceTodayByEmployee]);

  const tabs: TabItem[] = useMemo(
    () => [
      { id: 'all', label: t('common:filters.allStatus'), count: employees.length },
      {
        id: 'Active',
        label: t('common:status.active'),
        count: employees.filter((e) => e.status === 'Active').length,
      },
      {
        id: 'Inactive',
        label: t('common:status.inactive'),
        count: employees.filter((e) => e.status === 'Inactive').length,
      },
      {
        id: 'Terminated',
        label: t('employees:status.terminated'),
        count: employees.filter((e) => e.status === 'Terminated').length,
      },
    ],
    [employees, t],
  );

  const sortOptions: SortOption[] = useMemo(
    () => [
      { id: 'name', label: t('employees:sort.name') },
      { id: 'utilization', label: t('employees:sort.utilization') },
      { id: 'status', label: t('employees:sort.status') },
    ],
    [t],
  );

  const aggregates = useMemo(() => {
    const map = new Map<string, ReturnType<typeof aggregateEmployeeWeek>>();
    for (const emp of employees) {
      if (!emp.id) continue;
      map.set(emp.id, aggregateEmployeeWeek(emp.id, weekEntries, monthEntries, absences));
    }
    return map;
  }, [employees, weekEntries, monthEntries, absences]);

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    return employees
      .filter((e) => {
        if (tab !== 'all' && e.status !== tab) return false;
        if (!term) return true;
        return (
          e.firstName?.toLowerCase().includes(term) ||
          e.lastName?.toLowerCase().includes(term) ||
          e.fullName?.toLowerCase().includes(term) ||
          e.personalNumber?.toLowerCase().includes(term) ||
          e.email?.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        if (sortBy === 'utilization') {
          const ax = aggregates.get(a.id ?? '')?.utilization ?? 0;
          const bx = aggregates.get(b.id ?? '')?.utilization ?? 0;
          if (ax !== bx) return bx - ax;
        }
        if (sortBy === 'status') {
          const rank: Record<LiveStatus, number> = {
            in: 0,
            office: 1,
            break: 2,
            missing: 3,
            absent: 4,
            out: 5,
          };
          const la = deriveLiveStatus(a, dailyByEmployee.get(a.id ?? ''));
          const lb = deriveLiveStatus(b, dailyByEmployee.get(b.id ?? ''));
          if (rank[la] !== rank[lb]) return rank[la] - rank[lb];
        }
        const aName = `${a.lastName ?? ''} ${a.firstName ?? ''}`.trim();
        const bName = `${b.lastName ?? ''} ${b.firstName ?? ''}`.trim();
        return aName.localeCompare(bName);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, search, tab, sortBy, aggregates, dailyByEmployee, absenceTodayByEmployee]);

  const { page, pageCount, pageItems, goToPage } = usePagedList(filteredEmployees, undefined, [
    activePage,
    (next) => setListState({ page: next }),
  ]);

  // Export follows the filter, so the file mirrors what the list shows.
  const exportIds = useMemo(
    () =>
      filteredEmployees.length === employees.length
        ? undefined
        : filteredEmployees.map((e) => e.id).filter((id): id is string => !!id),
    [filteredEmployees, employees.length],
  );

  const handleCreateClick = () => {
    setDialogMode('create');
    setIsDialogOpen(true);
  };

  const handleEditClick = async (id: string) => {
    try {
      await fetchEmployeeById(id);
      setDialogMode('edit');
      setIsDialogOpen(true);
    } catch {
      addToast(t('employees:messages.loadError'), 'error');
    }
  };

  const handleSaveEmployee = async (data: EmployeeFormData) => {
    try {
      // Home location = start point in the planning assistant; saving never fails because of it.
      const home = await resolveHomeLocation(
        data.address,
        dialogMode === 'create' ? undefined : currentEmployee,
      );
      if (dialogMode === 'create') {
        await createEmployee({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          role: data.role,
          birthDate: data.birthDate || undefined,
          hireDate: data.hireDate || isoDate(new Date()),
          password: data.password || '',
          // why: personalNumber is required by CreateEmployeeRequest — always send it so the API
          // can report a per-field error. The address is optional (omitted when empty).
          personalNumber: data.personalNumber || '',
          socialSecurityNumber: data.socialSecurityNumber || undefined,
          iban: data.iban || undefined,
          bic: data.bic || undefined,
          address: toAddressPayload(data.address),
          ...(home.coords ?? {}),
          allowsHomeOffice: data.allowsHomeOffice,
          ...timeClockGroupForCreate(data),
        });
        addToast(t('employees:messages.created'), 'success');
      } else {
        if (!currentEmployee?.id) return;
        await updateEmployee(currentEmployee.id, {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          role: data.role,
          birthDate: data.birthDate || undefined,
          hireDate: data.hireDate || undefined,
          // why: personalNumber is required by UpdateEmployeeRequest — see the create branch.
          personalNumber: data.personalNumber || '',
          socialSecurityNumber: data.socialSecurityNumber || undefined,
          iban: data.iban || undefined,
          bic: data.bic || undefined,
          address: toAddressPayload(data.address),
          ...(home.coords ?? {}),
          ...(home.clear ? { clearHomeLocation: true } : {}),
          allowsHomeOffice: data.allowsHomeOffice,
          ...timeClockGroupForUpdate(data),
        });
        addToast(t('employees:messages.updated'), 'success');
      }
      if (home.failed) addToast(t('employees:messages.geocodeFailed'), 'warning');
      await fetchAllEmployees();
      setIsDialogOpen(false);
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { status?: number; data?: { errors?: Record<string, string[]> } };
      };
      if (axiosError?.response?.status === 400 && axiosError?.response?.data?.errors) {
        throw err;
      }
      addToast(
        dialogMode === 'create'
          ? t('employees:messages.createError')
          : t('employees:messages.updateError'),
        'error',
      );
      throw err;
    }
  };

  const handleDelete = (id: string, name: string) => {
    setEmployeeToDelete({ id, name });
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!employeeToDelete) return;
    try {
      await deleteEmployee(employeeToDelete.id);
      await fetchAllEmployees();
      addToast(t('employees:messages.deleted'), 'success');
    } catch {
      addToast(t('common:errors.deleteFailed'), 'error');
    } finally {
      setIsDeleteDialogOpen(false);
      setEmployeeToDelete(null);
    }
  };

  const handleActivate = async (id: string, name: string) => {
    try {
      await activateEmployee(id);
      await fetchAllEmployees();
      addToast(t('employees:messages.activated', { name }), 'success');
    } catch {
      addToast(t('common:errors.actionFailed'), 'error');
    }
  };

  const handleDeactivate = async (id: string, name: string) => {
    try {
      await deactivateEmployee(id);
      await fetchAllEmployees();
      addToast(t('employees:messages.deactivated', { name }), 'success');
    } catch {
      addToast(t('common:errors.actionFailed'), 'error');
    }
  };

  const handleResetPassword = (id: string, name: string, email?: string, phone?: string) => {
    setSelectedEmployeeForReset({ id, name, email, phone });
    setIsPasswordResetOpen(true);
  };

  const handlePasswordResetConfirm = async (newPassword: string) => {
    if (!selectedEmployeeForReset) return;
    try {
      await resetPassword(selectedEmployeeForReset.id, newPassword);
      addToast(t('employees:messages.passwordReset'), 'success');
    } catch {
      addToast(t('employees:messages.passwordResetError'), 'error');
      throw new Error('Password reset failed');
    }
  };

  if (loading && !initialLoadComplete) {
    return <EmployeesSkeleton />;
  }
  if (error && !initialLoadComplete) {
    return (
      <ListPageShell kicker={t('employees:kicker')} title={t('employees:title')}>
        <EmptyState
          variant="error"
          title={t('employees:messages.loadError')}
          description={error}
          onRetry={() => {
            clearError();
            fetchAllEmployees();
          }}
        />
      </ListPageShell>
    );
  }

  return (
    <div>
      <ListPageShell
        kicker={t('employees:kicker')}
        title={t('employees:title')}
        meta={t('employees:meta', {
          count: employees.length,
          in: liveCounts.inCount,
          missing: liveCounts.missingCount,
        })}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => exportExcel(exportIds)}
              loading={exporting}
              leftIcon={<Download className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {exporting
                ? t('common:excelImport.exporting')
                : exportIds
                  ? t('common:excelImport.exportFiltered', { total: exportIds.length })
                  : t('employees:actions.exportExcel')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsImportOpen(true)}
              disabled={exporting}
              leftIcon={<Upload className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('employees:actions.importExcel')}
            </Button>
            <Button
              onClick={handleCreateClick}
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('employees:newEmployee')}
            </Button>
          </>
        }
      >
        <KpiStripList items={kpis} />
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-3 sm:px-4">
            <TabStrip
              tabs={tabs}
              value={tab}
              onChange={(id) => setListState({ tab: id as TabId })}
              ariaLabel={t('employees:title')}
            />
          </div>
          <ListToolbar
            placeholder={t('employees:placeholders.search')}
            search={search}
            onSearchChange={(value) => setListState({ q: value })}
            sortValue={sortBy}
            onSortChange={(id) => setListState({ sort: id as SortId })}
            sortOptions={sortOptions}
            className="px-3 sm:px-4 py-2.5"
          />
        </div>

        {filteredEmployees.length === 0 ? (
          <EmptyState
            icon={<Users className="w-12 h-12" strokeWidth={1.75} />}
            title={
              search || tab !== 'all'
                ? t('employees:empty.titleFiltered')
                : t('employees:empty.title')
            }
            description={
              search || tab !== 'all'
                ? t('employees:empty.descriptionFiltered')
                : t('employees:empty.description')
            }
            action={
              !search && tab === 'all' ? (
                <Button
                  onClick={handleCreateClick}
                  leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('employees:newEmployee')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {/* Desktop: ListGrid */}
            <div className="hidden md:block">
              <ListGrid columns={COLUMNS} ariaLabel={t('employees:title')}>
                <ListGrid.Header>
                  <span>{t('employees:list.employee')}</span>
                  <span>{t('employees:list.contact')}</span>
                  <span>{t('employees:list.statusNow')}</span>
                  <span>{t('employees:list.currentlyAt')}</span>
                  <span className="flex items-center gap-1.5">
                    {t('employees:list.weekUtilization', { week: weekNumber })}
                    <HelpPopover label={t('common:actions.showExplanation')} text={t('employees:list.utilizationHelp')} />
                  </span>
                  <span />
                </ListGrid.Header>
                {pageItems.map((emp) => {
                  const daily = dailyByEmployee.get(emp.id ?? '');
                  const live = deriveLiveStatus(emp, daily);
                  const agg = aggregates.get(emp.id ?? '');
                  const plan = todayPlanByEmployee.get(emp.id ?? '');
                  const currentTask = plan?.tasks ? getCurrentTask(plan.tasks, nowMin) : null;
                  const absenceLabel = emp.id ? absenceTodayByEmployee.get(emp.id) : undefined;
                  return (
                    <ListGrid.Row
                      key={emp.id}
                      onClick={() => emp.id && navigate(`/employees/${emp.id}`)}
                    >
                      <div className="px-3 py-3 flex items-center gap-3 min-w-0">
                        <EmployeeAvatar
                          employee={{
                            firstName: emp.firstName,
                            lastName: emp.lastName,
                            avatarColor: emp.avatarColor,
                          }}
                          size={36}
                        />
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">
                            {emp.fullName ?? `${emp.firstName ?? ''} ${emp.lastName ?? ''}`.trim() ?? '—'}
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-slate-500">
                            <span className="font-mono-num">{emp.personalNumber ?? '—'}</span>
                            <span className="text-slate-400">·</span>
                            <span className="truncate">
                              {emp.role === 'Verwaltung'
                                ? t('employees:roles.administration')
                                : t('employees:roles.employee')}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="px-3 py-3 min-w-0 text-[12px]">
                        {emp.email && (
                          <div className="text-slate-600 truncate flex items-center gap-1.5">
                            <Mail className="w-3 h-3 text-slate-400" strokeWidth={2} aria-hidden />
                            <span className="truncate">{emp.email}</span>
                          </div>
                        )}
                        {emp.phone && (
                          <div className="text-slate-500 font-mono-num truncate flex items-center gap-1.5 mt-0.5">
                            <Phone className="w-3 h-3 text-slate-400" strokeWidth={2} aria-hidden />
                            <span className="truncate">{emp.phone}</span>
                          </div>
                        )}
                        {!emp.email && !emp.phone && (
                          <span className="text-slate-400">—</span>
                        )}
                      </div>

                      <div className="px-3 py-3 min-w-0">
                        <StaffStatusBadge status={live} />
                        {daily?.clockInTime && (live === 'in' || live === 'break' || live === 'office') && (
                          <div className="text-[10.5px] text-slate-400 mt-1 font-mono-num">
                            {t('employees:list.since')} {formatUtcToLocalTime(daily.clockInTime)}
                          </div>
                        )}
                      </div>

                      <div className="px-3 py-3 min-w-0">
                        <CurrentlyAt
                          currentTask={currentTask}
                          absenceLabel={absenceLabel}
                          isMissing={live === 'missing'}
                          missingHint={t('employees:list.missingHint')}
                        />
                      </div>

                      <div className="px-3 py-3 min-w-0">
                        {agg && <UtilizationCell agg={agg} t={t} />}
                      </div>

                      <div className="px-1 py-3 flex items-center justify-end">
                        <RowActions
                          status={emp.status}
                          onView={() => emp.id && navigate(`/employees/${emp.id}`)}
                          onEdit={() => handleEditClick(emp.id ?? '')}
                          onResetPassword={() =>
                            handleResetPassword(
                              emp.id ?? '',
                              emp.fullName ?? '',
                              emp.email ?? undefined,
                              emp.phone ?? undefined,
                            )
                          }
                          onActivate={() => handleActivate(emp.id ?? '', emp.fullName ?? '')}
                          onDeactivate={() => handleDeactivate(emp.id ?? '', emp.fullName ?? '')}
                          onDelete={() => handleDelete(emp.id ?? '', emp.fullName ?? '')}
                          t={t}
                        />
                      </div>
                    </ListGrid.Row>
                  );
                })}
              </ListGrid>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2">
              <div className="flex items-center justify-end gap-1.5 text-[11px] text-slate-500">
                {t('employees:list.weekUtilization', { week: weekNumber })}
                <HelpPopover label={t('common:actions.showExplanation')} text={t('employees:list.utilizationHelp')} />
              </div>
              {pageItems.map((emp) => {
                const daily = dailyByEmployee.get(emp.id ?? '');
                const live = deriveLiveStatus(emp, daily);
                const agg = aggregates.get(emp.id ?? '');
                const plan = todayPlanByEmployee.get(emp.id ?? '');
                const currentTask = plan?.tasks ? getCurrentTask(plan.tasks, nowMin) : null;
                const absenceLabel = emp.id ? absenceTodayByEmployee.get(emp.id) : undefined;
                return (
                  <div
                    key={emp.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => emp.id && navigate(`/employees/${emp.id}`)}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && emp.id) {
                        e.preventDefault();
                        navigate(`/employees/${emp.id}`);
                      }
                    }}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00A871]/40"
                  >
                    <div className="flex items-start gap-3">
                      <EmployeeAvatar
                        employee={{
                          firstName: emp.firstName,
                          lastName: emp.lastName,
                          avatarColor: emp.avatarColor,
                        }}
                        size={36}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-slate-900 text-[14px] truncate">
                            {emp.fullName ?? '—'}
                          </div>
                          <StaffStatusBadge status={live} compact />
                        </div>
                        <div className="text-[11px] text-slate-500 truncate font-mono-num">
                          {emp.personalNumber ?? '—'}
                          {emp.role && (
                            <>
                              <span className="mx-1 text-slate-400">·</span>
                              <span>
                                {emp.role === 'Verwaltung'
                                  ? t('employees:roles.administration')
                                  : t('employees:roles.employee')}
                              </span>
                            </>
                          )}
                        </div>
                        <div className="mt-1.5">
                          <CurrentlyAt
                            currentTask={currentTask}
                            absenceLabel={absenceLabel}
                            isMissing={live === 'missing'}
                            missingHint={t('employees:list.missingHint')}
                          />
                        </div>
                        {agg && agg.targetHours > 0 && (
                          <div className="mt-2">
                            <UtilizationCell agg={agg} t={t} compact />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <Pagination
              page={page}
              pageCount={pageCount}
              totalCount={filteredEmployees.length}
              onPageChange={goToPage}
              disabled={loading}
            />
          </>
        )}
      </ListPageShell>

      <EmployeeDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSaveEmployee}
        employee={currentEmployee}
        mode={dialogMode}
      />

      {/* Excel round-trip. New accounts get a one-time password, shown once after import. */}
      <ExcelImportModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        entity="employees"
        title={t('employees:actions.importTitle')}
        onImported={() => fetchAllEmployees()}
      />

      <PasswordResetDialog
        isOpen={isPasswordResetOpen}
        onClose={() => {
          setIsPasswordResetOpen(false);
          setSelectedEmployeeForReset(null);
        }}
        onReset={handlePasswordResetConfirm}
        employeeName={selectedEmployeeForReset?.name || ''}
        employeeEmail={selectedEmployeeForReset?.email}
        employeePhone={selectedEmployeeForReset?.phone}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setEmployeeToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('employees:deleteEmployee')}
        message={t('employees:confirmDelete', { name: employeeToDelete?.name || '' })}
        confirmText={t('common:actions.delete')}
        cancelText={t('common:actions.cancel')}
        variant="danger"
      />
    </div>
  );
}

interface CurrentlyAtProps {
  currentTask: { objectName?: string | null; objectShortName?: string | null; objectId?: string } | null;
  absenceLabel?: string;
  isMissing: boolean;
  missingHint: string;
}

function CurrentlyAt({ currentTask, absenceLabel, isMissing, missingHint }: CurrentlyAtProps) {
  if (currentTask) {
    const label = currentTask.objectShortName ?? currentTask.objectName ?? '—';
    const colors = getObjectColors(currentTask.objectId ?? label);
    return (
      <span
        className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px] font-medium"
        style={{ background: colors.soft, color: colors.ink }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: colors.color }} />
        <span className="truncate max-w-[10rem]">{label}</span>
      </span>
    );
  }
  if (absenceLabel) {
    return <span className="text-[11.5px] text-slate-500">{absenceLabel}</span>;
  }
  if (isMissing) {
    return (
      <span className="text-[11.5px] text-red-600 inline-flex items-center gap-1">
        <AlertTriangle className="w-3 h-3" strokeWidth={2} aria-hidden />
        {missingHint}
      </span>
    );
  }
  return <span className="text-[11.5px] text-slate-400">—</span>;
}

interface UtilizationCellProps {
  agg: ReturnType<typeof aggregateEmployeeWeek>;
  t: (key: string, opts?: Record<string, unknown>) => string;
  compact?: boolean;
}

function UtilizationCell({ agg, t, compact = false }: UtilizationCellProps) {
  const pct = agg.targetHours > 0 ? Math.min(100, agg.utilization * 100) : 0;
  const pctRaw = Math.round(agg.utilization * 100);
  const tone =
    agg.utilization >= 0.98
      ? 'bg-emerald-500'
      : agg.utilization >= 0.7
      ? 'bg-amber-500'
      : 'bg-orange-500';
  const toneText =
    agg.utilization >= 0.98
      ? 'text-emerald-600'
      : agg.utilization >= 0.7
      ? 'text-amber-600'
      : 'text-orange-600';
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="font-mono-num text-slate-600">
          {formatHoursDecimal(agg.weeklyHours)}
          <span className="text-slate-400">/{formatHoursDecimal(agg.targetHours)}h</span>
        </span>
        <span className={`font-mono-num font-medium ${toneText}`}>{pctRaw}%</span>
      </div>
      <div
        className="w-full rounded-full bg-slate-100 overflow-hidden"
        style={{ height: 4 }}
      >
        <div
          className={`h-full ${tone} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {!compact && (
        <div className="text-[10px] text-slate-400 mt-1 font-mono-num">
          {t('employees:month.summary', {
            done: Math.round(agg.monthHours),
            target: Math.round(agg.monthTarget),
          })}
          {agg.absenceDays > 0 && (
            <span className="ml-2 text-amber-600">
              · {t('employees:month.absences', { days: agg.absenceDays })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function StaffStatusBadge({ status, compact: _compact = false }: { status: LiveStatus; compact?: boolean }) {
  const map: Record<LiveStatus, { fg: string; bg: string; dot: string; key: string }> = {
    in:      { fg: 'text-emerald-700', bg: 'bg-emerald-50 border border-emerald-200', dot: 'bg-emerald-500', key: 'in' },
    break:   { fg: 'text-amber-700',   bg: 'bg-amber-50 border border-amber-200',     dot: 'bg-amber-500',   key: 'break' },
    absent:  { fg: 'text-sky-700',     bg: 'bg-sky-50 border border-sky-200',         dot: 'bg-sky-500',     key: 'absent' },
    missing: { fg: 'text-red-700',     bg: 'bg-red-50 border border-red-200',         dot: 'bg-red-500',     key: 'missing' },
    office:  { fg: 'text-sky-700',     bg: 'bg-sky-50 border border-sky-200',         dot: 'bg-sky-500',     key: 'office' },
    out:     { fg: 'text-slate-600',   bg: 'bg-slate-100 border border-slate-200',    dot: 'bg-slate-400',   key: 'out' },
  };
  const tone = map[status];
  const { t } = useTranslation('employees');
  void _compact;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full text-[10.5px] font-medium px-2 py-0.5 ${tone.fg} ${tone.bg}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} aria-hidden />
      {t(`live.${tone.key}`)}
    </span>
  );
}

interface RowActionsProps {
  status: string | null | undefined;
  onView: () => void;
  onEdit: () => void;
  onResetPassword: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onDelete: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function RowActions({
  status,
  onView,
  onEdit,
  onResetPassword,
  onActivate,
  onDeactivate,
  onDelete,
  t,
}: RowActionsProps) {
  return (
    <div
      className="opacity-60 group-hover:opacity-100 group-focus-within:opacity-100 flex items-center gap-0.5 transition-opacity"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onView}
        title={t('employees:actions.viewDetails', { defaultValue: 'Anzeigen' })}
        aria-label={t('employees:actions.viewDetails', { defaultValue: 'Anzeigen' })}
        className="w-7 h-7 grid place-items-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100"
      >
        <Eye className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </button>
      <button
        type="button"
        onClick={onEdit}
        title={t('employees:actions.edit', { defaultValue: 'Bearbeiten' })}
        aria-label={t('employees:actions.edit', { defaultValue: 'Bearbeiten' })}
        className="w-7 h-7 grid place-items-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100"
      >
        <Pencil className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </button>
      <button
        type="button"
        onClick={onResetPassword}
        title={t('employees:actions.resetPassword', { defaultValue: 'Passwort' })}
        aria-label={t('employees:actions.resetPassword', { defaultValue: 'Passwort' })}
        className="w-7 h-7 grid place-items-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100"
      >
        <Key className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </button>
      {status === 'Active' ? (
        <button
          type="button"
          onClick={onDeactivate}
          title={t('employees:actions.deactivate', { defaultValue: 'Deaktivieren' })}
          aria-label={t('employees:actions.deactivate', { defaultValue: 'Deaktivieren' })}
          className="w-7 h-7 grid place-items-center rounded text-amber-600 hover:text-amber-700 hover:bg-amber-50"
        >
          <UserX className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
        </button>
      ) : status === 'Inactive' ? (
        <button
          type="button"
          onClick={onActivate}
          title={t('employees:actions.activate', { defaultValue: 'Aktivieren' })}
          aria-label={t('employees:actions.activate', { defaultValue: 'Aktivieren' })}
          className="w-7 h-7 grid place-items-center rounded text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
        >
          <UserCheck className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
        </button>
      ) : null}
      {status !== 'Terminated' && (
        <button
          type="button"
          onClick={onDelete}
          title={t('common:actions.delete', { defaultValue: 'Löschen' })}
          aria-label={t('common:actions.delete', { defaultValue: 'Löschen' })}
          className="w-7 h-7 grid place-items-center rounded text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
        </button>
      )}
    </div>
  );
}

function EmployeesSkeleton() {
  return (
    <div className="space-y-4 sm:space-y-6 animate-pulse">
      <div className="h-[88px] rounded-xl border border-slate-200 bg-white" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 rounded-xl border border-slate-200 bg-white" />
        ))}
      </div>
      <div className="h-[78px] rounded-xl border border-slate-200 bg-white" />
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-16 border-t border-slate-100 first:border-t-0 bg-slate-50/50" />
        ))}
      </div>
    </div>
  );
}
